// @fileoverview This is a composable for the photo album editor.
// It is used to handle the photo album editor's state and actions.
// It is also used to handle the photo album editor's content picker sources and search modes.

import { ContentPickerSource } from '@@/bits/content_picker'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { isFileWithImageFileExtension } from '@@/bits/post_attachment'
import { getAttachmentsFromDraft } from '@@/bits/post_drafts'
import { isImageUrl } from '@@/bits/url'
import { SnackbarNotificationType } from '@@/enums'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import type { Cid, PostAttributes, WishAttachment } from '@@/types'
import KeyboardShortcutTooltip from '@@/vuecomponents/KeyboardShortcutTooltip.vue'
import useLogger from '@@/vuecomposables/logger'
import { getUndoDeleteShortcut } from '@@/vuecomposables/undoable'
import { usePostAttachmentUploader } from '@@/vuecomposables/usePostAttachmentUploader'
import { createSharedComposable } from '@vueuse/core'
import { ref } from 'vue'

const PLACEHOLDER_UPLOADING_ATTACHMENT = { url: '', progress: 0, isUploadingFile: true }
const logger = useLogger('photo-album-editor', { logLifecycleHooks: false, log: true })

export const usePhotoAlbumEditor = createSharedComposable(() => {
  const contentPickerStore = useContentPickerStore()
  const surfaceContentPickerStore = useSurfaceContentPickerStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const currentItemIndex = ref(0)

  /**
   * Track whether the photo album editor dialog was open before the image picker was shown
   * This is used to restore the editor dialog state when the image picker is closed without selection
   */
  const wasEditorDialogOpenBeforeImagePicker = ref(false)

  const updatePhotoAlbumAttachmentIndex = (index: number): void => {
    currentItemIndex.value = index
  }

  // #region Photo album image picker and photo editor dialog visibility
  const surfacePanelsStore = useSurfacePanelsStore()
  const showPhotoAlbumImagePicker = (): void => {
    surfaceContentPickerStore.hidePostContentPicker()
    // Track if the editor dialog was open before showing the image picker
    wasEditorDialogOpenBeforeImagePicker.value = surfacePanelsStore.xPhotoAlbumEditorDialog
    // Hide the photo album editor dialog when showing the image picker
    surfacePanelsStore.xPhotoAlbumEditorDialog = false
    surfacePanelsStore.xPhotoAlbumImagePicker = true
    contentPickerStore.showContentSourceMenu({ contentPickerContext: 'photo-album-image-picker' })
  }

  const hidePhotoAlbumImagePicker = (): void => {
    surfacePanelsStore.xPhotoAlbumImagePicker = false
    contentPickerStore.hideContentSourceMenu()

    // Only show the photo album editor dialog if it was open before the image picker
    if (wasEditorDialogOpenBeforeImagePicker.value) {
      showPhotoAlbumEditorDialog()
    }
    // Reset the selection tracking
    wasEditorDialogOpenBeforeImagePicker.value = false
  }

  const showPhotoAlbumEditorDialog = (): void => {
    surfacePanelsStore.xPhotoAlbumEditorDialog = true
  }

  const hidePhotoAlbumEditorDialog = (): void => {
    surfacePanelsStore.xPhotoAlbumEditorDialog = false
  }
  // #endregion

  // #region Image upload logic
  const surfaceStore = useSurfaceStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const { startUploadFile, stopUploadFile } = usePostAttachmentUploader()

  const getDraftAndCid = (postCid?: Cid): { draft: PostAttributes | null; cid?: Cid } => {
    if (postCid == null) {
      return { draft: surfaceDraftsStore.activeDraft, cid: surfaceDraftsStore.activeDraft?.cid }
    }
    return { draft: surfaceDraftsStore.getDraftFromCid(postCid), cid: postCid }
  }

  /**
   * Sets placeholder attachments in a draft up to the specified index
   * Used to show upload progress states while files are being uploaded
   * @param draft - The post draft to add placeholder attachments to
   * @param file - The file being uploaded. If provided, it will be set as the file being uploaded for the last placeholder attachment.
   */
  const setPlaceholderAttachments = (draft: PostAttributes, file?: File): void => {
    const existingAttachments = getAttachmentsFromDraft(draft)
    const targetIndex = existingAttachments.length
    const attachments = [...existingAttachments]
    while (attachments.length <= targetIndex) {
      attachments.push(PLACEHOLDER_UPLOADING_ATTACHMENT)
    }
    if (file != null) {
      attachments[attachments.length - 1].fileBeingUploaded = file
    }

    surfaceDraftsStore.updateDraft({
      attachments,
      cid: draft.cid,
    })
  }

  const onUploadStart = ({
    file,
    url, // for Google Drive. unnecessary for now.
    postCid,
    index,
  }: {
    file?: File
    url?: string
    postCid?: Cid
    index?: number
  }): void => {
    logger.log('onUploadStart', { file, url, postCid, index })

    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null || file == null) return

    const attachments = getAttachmentsFromDraft(draft)
    setPlaceholderAttachments(draft, file)

    // TODO: use custom max file upload size if given
    const maxFileUploadSize = surfaceStore.uploadLimit
    startUploadFile({ cid, file, attachmentIndex: index ?? attachments.length, maxFileUploadSize })
  }

  const onUploadProgress = ({
    progress,
    postCid,
    index,
  }: {
    progress: number
    postCid?: Cid
    index?: number
  }): void => {
    logger.log('onUploadProgress', { progress, postCid, index })
    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null) return
    const attachments = getAttachmentsFromDraft(draft)
    attachments[index ?? attachments.length].progress = progress
    surfaceDraftsStore.updateDraft({ attachments, cid })
  }

  const onUploadCancel = (postCid?: Cid, index?: number): void => {
    logger.log('onUploadCancel', { postCid, index })
    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null) return
    const attachments = getAttachmentsFromDraft(draft)
    stopUploadFile({ cid, attachmentIndex: index ?? attachments.length })
    const updatedAttachments = [...attachments]
    updatedAttachments.splice(index ?? attachments.length, 1)
    surfaceDraftsStore.updateDraft({
      attachments: updatedAttachments,
      cid,
    })
  }

  const onUploadDone = (url: string, payload?: { altText?: string; postCid?: Cid }): void => {
    logger.log('onUploadDone', { url, payload })
    const { draft, cid } = getDraftAndCid(payload?.postCid)
    if (draft == null || cid == null) return
    const attachments = [...getAttachmentsFromDraft(draft), { url, alt: payload?.altText }]

    // discard progress states of attachments
    surfaceDraftsStore.updateDraft({
      attachments: attachments.map((a) => ({
        id: a.id,
        url: a.url,
        alt: a.alt,
        signed_url: a.signed_url,
        hotlink: a.hotlink,
      })),
      cid,
    })
    currentItemIndex.value = attachments.length - 1
  }

  const onUploadError = (errorSource?: File | any): void => {
    logger.error('onUploadError', { errorSource })
  }

  const selectFile = ({ file }: { file: File }): void => {
    logger.log('selectFile', { file })
    onUploadStart({ file })
  }

  const selectLink = async (
    url: string,
    payload: { altText?: string; contentSource: ContentPickerSource },
  ): Promise<void> => {
    logger.log('selectLink', { url, contentSource: payload.contentSource })
    const { draft, cid } = getDraftAndCid()
    if (draft == null || cid == null) return
    const attachments = getAttachmentsFromDraft(draft)

    // Store the original attachments to revert to this state if URL is invalid
    // This prevents removing existing photos when adding an invalid URL
    const originalAttachments = [...attachments]

    if (attachments.length === 0) {
      surfaceDraftsStore.updateDraft({
        attachments: [{ url, alt: payload.altText }],
        attachment: url,
        wish_content: {
          attachment_props: { alternative_text: payload.altText },
          is_processed: false,
        },
        cid,
      })
    } else {
      onUploadDone(url, payload)
    }

    const skipImageValidation =
      payload.contentSource === ContentPickerSource.ImageSearch ||
      payload.contentSource === ContentPickerSource.GifSearch

    if (skipImageValidation) {
      return
    }

    const isValid = await isImageUrl(url)
    if (!isValid) {
      globalSnackbarStore.setSnackbar({
        message: __('Invalid image URL'),
        notificationType: SnackbarNotificationType.error,
      })
      // revert to the original state before adding the invalid URL
      surfaceDraftsStore.updateDraft({
        attachments: originalAttachments,
        cid,
      })
    }
  }

  const selectFiles = ({ files }: { files: File[] }): void => {
    const { draft, cid } = getDraftAndCid()
    if (draft == null || cid == null) return
    logger.log('selectFiles', { files, draft, cid })

    // Filter files to only include valid image files
    const validFiles: File[] = []
    const invalidFiles: File[] = []

    files.forEach((file) => {
      if (isFileWithImageFileExtension(file)) {
        validFiles.push(file)
      } else {
        invalidFiles.push(file)
      }
    })

    // Show error snackbar if there are invalid files
    if (invalidFiles.length > 0) {
      globalSnackbarStore.setSnackbar({
        message: __('%{numInvalidFiles} invalid files are skipped for adding to photo album', {
          numInvalidFiles: invalidFiles.length,
        }),
        notificationType: SnackbarNotificationType.error,
      })
    }

    // Continue with valid files only
    const attachments = getAttachmentsFromDraft(draft)
    validFiles.forEach((file, index) => {
      onUploadStart({ file, index: index + attachments.length, postCid: cid })
    })
  }
  // #endregion

  // #region Delete and undo functionality for photo album images
  interface DeletedAttachmentRecord {
    attachment: WishAttachment
    index: number
    snackbarId: string
  }

  const deletedAttachmentRecords = ref<DeletedAttachmentRecord[]>([])

  /**
   * Deletes an image from the photo album at the specified index
   * Shows a snackbar with undo option
   * @param params - Object containing deletion parameters
   * @param params.index - The index of the image to delete
   * @param params.postCid - Optional post CID, defaults to active draft
   * @param params.actionText - Optional custom action text for the undo button
   * @returns Object containing the snackbar ID and deleted attachment
   */
  const deletePhotoAlbumImage = (params: {
    index: number
    postCid?: Cid
    actionText?: string
  }): { snackbarId: string; deletedAttachment: WishAttachment } | null => {
    const { index, postCid, actionText } = params
    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null) return null

    const attachments = getAttachmentsFromDraft(draft)
    if (index < 0 || index >= attachments.length) return null

    const newAttachments = [...attachments]
    const [deletedAttachment] = newAttachments.splice(index, 1)

    if (newAttachments.length === 0) {
      surfaceDraftsStore.updateDraft({
        attachments: [],
        attachment: '',
        cid,
      })
    } else {
      surfaceDraftsStore.updateDraft({
        attachments: newAttachments,
        attachment: newAttachments[0].url,
        cid,
      })
    }

    const snackbarId = globalSnackbarStore.setSnackbar({
      message: __('Image deleted'),
      notificationType: SnackbarNotificationType.success,
      actionText: actionText || __('Undo'),
      actionContentSlot: device.desktop ? KeyboardShortcutTooltip : undefined,
      actionContentSlotProps: { shortcut: getUndoDeleteShortcut() },
      actionTextActions: [
        () => {
          undoDeletePhotoAlbumImage({ snackbarId, postCid })
        },
      ],
    })

    deletedAttachmentRecords.value.push({
      attachment: deletedAttachment,
      index,
      snackbarId,
    })

    return { snackbarId, deletedAttachment }
  }

  /**
   * Undoes the deletion of a photo album image
   * @param params - Object containing undo parameters
   * @param params.snackbarId - Optional snackbar ID to undo specific deletion, otherwise undoes the last one
   * @param params.postCid - Optional post CID, defaults to active draft
   */
  const undoDeletePhotoAlbumImage = (params: { snackbarId?: string; postCid?: Cid }): void => {
    if (deletedAttachmentRecords.value.length === 0) return

    const { snackbarId, postCid } = params
    const record = snackbarId
      ? deletedAttachmentRecords.value.find((r) => r.snackbarId === snackbarId)
      : deletedAttachmentRecords.value[deletedAttachmentRecords.value.length - 1]

    if (!record) return

    const { draft, cid } = getDraftAndCid(postCid)
    if (draft == null || cid == null) return

    const attachments = getAttachmentsFromDraft(draft)
    const newAttachments = [...attachments]
    newAttachments.splice(record.index, 0, record.attachment)

    surfaceDraftsStore.updateDraft({
      attachments: newAttachments,
      attachment: newAttachments[0].url,
      cid,
    })

    globalSnackbarStore.removeSnackbar(record.snackbarId)
    const recordIndex = deletedAttachmentRecords.value.findIndex((r) => r.snackbarId === record.snackbarId)
    if (recordIndex !== -1) {
      deletedAttachmentRecords.value.splice(recordIndex, 1)
    }
  }

  /**
   * Clears all deleted attachment records
   * Should be called when closing the photo album editor or switching contexts
   */
  const clearDeletedAttachmentRecords = (): void => {
    deletedAttachmentRecords.value = []
  }
  // #endregion

  return {
    showPhotoAlbumImagePicker,
    hidePhotoAlbumImagePicker,
    showPhotoAlbumEditorDialog,
    hidePhotoAlbumEditorDialog,

    onUploadStart,
    onUploadProgress,
    onUploadDone,
    onUploadCancel,
    onUploadError,
    selectFile,
    selectFiles,
    selectLink,

    currentItemIndex,
    updatePhotoAlbumAttachmentIndex,

    // Delete and undo functionality
    deletePhotoAlbumImage,
    undoDeletePhotoAlbumImage,
    clearDeletedAttachmentRecords,
    deletedAttachmentRecords,
  }
})
